import React from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import useAppSelector from '@/hooks/useAppSelector'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { useCurrentUserOrFail } from '@/hooks/useCurrentUserOrFail'
import {
  EMAIL_CONTENT_TYPE,
  EMAIL_SERVICE_TYPE,
  EXTERNAL_LINKS,
} from '@/lib/constants'
import companyService from '@/lib/services/companyService'
import {
  selectEmailContentType,
  selectEmailService,
} from '@/redux/slices/session'

/**
 * ⚠️⚠️⚠️ If making changes in here, you should also make changes
 * in the invite-referral-partner.html.twig email template in the RQ-API project
 */
const InviteReferralPartnerEmailPreview: React.FC = () => {
  const currentUser = useCurrentUserOrFail()
  const currentCompany = useCurrentCompanyOrFail()
  const emailContentType = useAppSelector(selectEmailContentType)
  const emailServiceType = useAppSelector(selectEmailService)

  const isUsingPersonalOrCompanyEmail =
    emailContentType === EMAIL_CONTENT_TYPE.PERSONAL ||
    emailContentType === EMAIL_CONTENT_TYPE.COMPANY

  const isUsingPersonalOrCompanyEmailViaNylas =
    isUsingPersonalOrCompanyEmail &&
    emailServiceType === EMAIL_SERVICE_TYPE.NYLAS

  const inviteLink = companyService.getInvitationLink(
    currentCompany.referralCode,
  )

  return (
    <div style={{ lineHeight: 1.75 }}>
      <Card>
        <CardContent>
          <p className="mb-4">Hi,</p>

          {isUsingPersonalOrCompanyEmailViaNylas ? (
            <p className="mb-4">
              I'd like to invite you to join me on RQ — the professional
              referral platform.
            </p>
          ) : (
            <p className="mb-4">
              {currentUser.fullName} from {currentCompany.presentationName} has
              invited you to join them on RQ — the professional referral
              platform.
            </p>
          )}

          <p className="mb-4">
            You can claim your free account here:{' '}
            <a href={inviteLink} target="_blank" rel="noreferrer">
              {inviteLink}
            </a>
          </p>

          <div className="mb-8 text-center">
            <Button asChild>
              <a href={inviteLink} target="_blank" rel="noreferrer">
                Accept invite
              </a>
            </Button>
          </div>

          <p className="mb-0">
            With RQ, {isUsingPersonalOrCompanyEmailViaNylas ? 'we' : 'you'} can:
          </p>

          <ul style={{ marginTop: 0 }} className="mb-4">
            <li>Refer clients securely and compliantly</li>
            <li>Track every case in real time</li>
            <li>Strengthen professional relationships</li>
            <li>Unlock new revenue from referrals</li>
          </ul>

          <p className="mb-4">
            It’s quick to join, easy to use, and built for you to collaborate
            more closely on shared clients.
          </p>

          <p className="mb-4">
            You can learn more about RQ on its website:{' '}
            <a
              href={EXTERNAL_LINKS.marketingWebsite}
              title="Learn more about RQ"
            >
              https://www.rq.app
            </a>
            .
          </p>

          <p className="mb-0">
            Best wishes,
            <br />
            <strong>The RQ Team</strong>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

export default InviteReferralPartnerEmailPreview
